import React from 'react';
import { Link } from 'react-router-dom';
// Material UI Imports
import { Tabs } from '@mui/material';
import LibraryIcon from '@mui/icons-material/AutoStories';
import ReportsIcon from '@mui/icons-material/Assignment';
import RoomIcon from '@mui/icons-material/Room';
import TimerIcon from '@mui/icons-material/Timer';
import ListIcon from '@mui/icons-material/ViewList';
import DataIcon from '@mui/icons-material/InsertChart';
import BurstMode from '@mui/icons-material/BurstMode';
import Assignment from '@mui/icons-material/Assignment';
import ThumbsUpDown from '@mui/icons-material/ThumbsUpDown';
import LocationIcon from '@mui/icons-material/PinDrop';
import LotIcon from '@mui/icons-material/Home';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
// Custom Imports
import { TasksIcon, TaskReportIcon, DivisionIcon, CommunityIcon } from 'global/components/PerformIcons';
import logo from 'assets/logo.svg';
import locationGif from 'assets/location-selection-demo.gif';
import { get } from 'global/lib/lodash';
import {
  HomeIconBox,
  HomeInlineIcon,
  HomePerformLogo,
  HomePerformLogoWrapper,
  HomeGif,
  HomeGifWrapper,
  HomeNoWrap,
  HomeFlexIcons,
  HomeColorTab,
} from './Home.styles';

export const welcomeItems = (opts) => [
  {
    title: 'Welcome',
    label: <></>,
    longText: (
      <>
        <HomePerformLogoWrapper>
          <HomePerformLogo src={logo} alt="" />
        </HomePerformLogoWrapper>
        <p>Welcome to PERFORM!</p>
        <p>
          We're committed to advancing the home building industry and making things easier for you. We hope you find PERFORM useful for your daily tasks, whether that's construction quality, jobsite
          safety, home readiness, or warranty.
        </p>
        <p>
          Click <em>next</em> to explore the available features.
        </p>
      </>
    ),
  },
  {
    title: 'Location',
    label: (
      <>
        <HomeIconBox as={LocationIcon} />
        <HomeIconBox>Location</HomeIconBox>
      </>
    ),
    longText: (
      <>
        <p>
          <strong>Set your location</strong> by using the menu in the upper right hand corner.
        </p>
        <HomeGifWrapper>
          <HomeGif src={locationGif} alt="" />
        </HomeGifWrapper>
        <p>
          The site will show <strong>only what's relevant</strong> to your location, whether it's a{' '}
          <HomeNoWrap>
            <HomeInlineIcon as={DivisionIcon} /> division
          </HomeNoWrap>
          ,{' '}
          <HomeNoWrap>
            <HomeInlineIcon as={CommunityIcon} /> community
          </HomeNoWrap>
          , or{' '}
          <HomeNoWrap>
            <HomeInlineIcon as={LotIcon} /> lot
          </HomeNoWrap>
          . You can change your location at any time to change what you see.
        </p>
      </>
    ),
  },
];

export const homeItems = (opts) => [
  {
    title: 'Tasks',
    link: '/tasks',
    label: (
      <>
        <HomeIconBox as={TasksIcon} />
        <HomeIconBox>Tasks</HomeIconBox>
      </>
    ),
    canDisplay: () => get(opts, 'nav.tasks'),
    text: 'Manage issues and action plans in one place',
    longText: (
      <>
        <p>
          Use <strong>tasks</strong> to help track and resolve issues on the jobsite. Take a picture, write a comment, and assign tasks to anyone in the system. They will be notified, and you can
          carry on with your day.
        </p>
        <p>Tasks can be created on-the-go or following an assessment, but they're all tracked in one place for quick access.</p>
        <Tabs color="primary" value={false} centered>
          <HomeColorTab component={Link} to="/tasks/list" icon={<RoomIcon />} label="by Location" />
          <HomeColorTab component={Link} to="/tasks/scorecard" icon={<TimerIcon />} label="by List" />
        </Tabs>
        <p>
          By default, tasks are organized <em>by location</em>, but tasks can also be viewed all together <em>by list</em>.
        </p>
      </>
    ),
  },
  {
    title: 'Assessments',
    link: '/reports',
    label: (
      <>
        <HomeIconBox as={ReportsIcon} />
        <HomeIconBox>Assessments</HomeIconBox>
      </>
    ),
    canDisplay: () => get(opts, 'nav.assessments'),
    text: 'Create and review inspections or audits',
    longText: (
      <>
        <p></p>
        <p>
          Create <strong>assessments</strong> for conducting audits or inspections. You can conduct an assessment on a single building, across a community or over an entire division.
        </p>
        <p>For items of concern, create tasks and assign them to ensure they get addressed.</p>
        <Tabs color="primary" value={false} centered>
          <HomeColorTab component={Link} to="/reports/list" icon={<ListIcon />} label="List" />
          <HomeColorTab component={Link} to="/reports/data" icon={<DataIcon />} label="Data" />
        </Tabs>
        <p>The default view shows a list of assessments. Use the data tab to download observation data for analytics.</p>
      </>
    ),
  },
  {
    title: 'Library',
    link: '/library',
    canDisplay: () => get(opts, 'nav.library'),
    label: (
      <>
        <HomeIconBox as={LibraryIcon} />
        <HomeIconBox>Library</HomeIconBox>
      </>
    ),
    text: 'Browse relevant construction details, instruction, and training',
    longText: (
      <>
        <p></p>
        <p>
          Search a reference <strong>library</strong> of your company's performance expectations. Use filters or keywords to quickly find what you're looking for.
        </p>
        <p>Documents are organized by editions, and then by area of concern.</p>
        <HomeFlexIcons>
          <HomeIconBox as={TaskReportIcon} color="primary" />
          <HomeIconBox as={BurstMode} color="primary" />
          <HomeIconBox as={Assignment} color="primary" />
          <HomeIconBox as={ThumbsUpDown} color="primary" />
        </HomeFlexIcons>
        <p>Although checklists are the most common type of documentation, many different types of documents can be housed in the system.</p>
      </>
    ),
  },
  {
    title: 'Agent',
    link: '/agent',
    canDisplay: () => get(opts, 'nav.agent'),
    label: (
      <>
        <HomeIconBox as={AutoAwesomeIcon} />
        <HomeIconBox>Agent</HomeIconBox>
      </>
    ),
    text: 'Ask questions and continue agent conversations',
    longText: (
      <>
        <p>
          Use the <strong>agent</strong> to ask questions about your accessible locations, assessments, tasks, and related work.
        </p>
        <p>Conversations can be revisited later, so you can continue a thread instead of starting over.</p>
      </>
    ),
  },
];

export default homeItems;
